import { useState, useEffect, useMemo, useCallback, useRef, memo } from "react";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { useGlobalState } from "../GlobalContext";
import RetrieveProductSettings from "../ProductSettings/RetrieveProductSettings";
import ProductSettings from "../ProductSettings/ProductSettings";
import { getSettingsId } from "../ProductSettings/Helper";
import { apiLabelUrl } from "../Functions/functions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowUp, faXmark, faPlus } from "@fortawesome/pro-regular-svg-icons";
import ModelViewer from "../ModelViewer/ModelViewer";
import fetchLibraryProductPrice from "./fetchLibraryProductPrice";

const DEFAULT_PRESET_COLORS = ["#022955", "#360255", "#00924d", "#e67e22", "#e74c3c", "#3498db", "#9b59b6", "#1abc9c"];

// Isolated viewer that never re-renders after mount
const LibraryModelViewer = memo(
    function LibraryModelViewer({ modelUrl, extension, containerId, screenshotFnRef }) {
        return (
            <div id={containerId}>
                <div className="modelviewer-stats"></div>
                <ModelViewer
                    model={modelUrl}
                    extension={extension}
                    container={containerId}
                    width={250}
                    newModel={true}
                    preserveDrawingBuffer={true}
                    hideGrid={true}
                    screenshotFnRef={screenshotFnRef}
                />
            </div>
        );
    },
    () => true
); // Always skip re-render — props never change for the same upload

export default function LibraryAddProduct({
    onClose,
    onProductAdded,
    onLabelCreated,
    textData,
    labels = [],
    products = [],
}) {
    const { productSettings, setProductSettings, setMandatoryProductSettings, loggedInUser, labelLogo } =
        useGlobalState();

    const rawPresetColors = loggedInUser?.company?.label_preset_colors;
    const presetColors = rawPresetColors?.length
        ? rawPresetColors.map((c) => (c && typeof c === "object" ? c.color : c)).filter(Boolean)
        : DEFAULT_PRESET_COLORS;

    // Upload state
    const [file, setFile] = useState(null);
    const [fileExtension, setFileExtension] = useState(null);
    const [modelPreviewUrl, setModelPreviewUrl] = useState(null);
    const viewerContainerRef = useRef(null);
    const screenshotFnRef = useRef(null);

    // Product state
    const [productName, setProductName] = useState("");
    const [defaultQuantity, setDefaultQuantity] = useState(1);
    const [selectedLabelIds, setSelectedLabelIds] = useState([]);

    // Settings state
    const [chosenConfig, setChosenConfig] = useState({
        application: null,
        material: null,
        color: null,
        polish: null,
        coating: null,
        orientation: null,
        sterile: null,
        intendedUse: null,
        clinicalUse: null,
        custom: null,
    });

    // Inline label creation
    const [showNewLabel, setShowNewLabel] = useState(false);
    const [newLabelName, setNewLabelName] = useState("");
    const [newLabelColor, setNewLabelColor] = useState(presetColors[0]);
    const [savingLabel, setSavingLabel] = useState(false);

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const modelViewerContainerId = "library-model-preview";

    // Load product settings if not already in context
    useEffect(() => {
        if (!productSettings || productSettings.length === 0) {
            RetrieveProductSettings({ setProductSettings, setMandatoryProductSettings });
        }
    }, []);

    // Check if current settings resolve to a valid settings_id
    const settingsId = useMemo(() => {
        if (!productSettings || productSettings.length === 0) return "";
        return getSettingsId(chosenConfig, productSettings);
    }, [chosenConfig, productSettings]);

    // Price calculation
    const [price, setPrice] = useState(null);
    const [priceLoading, setPriceLoading] = useState(false);
    const [isCustom, setIsCustom] = useState(false);

    // Can save?
    const canSave = file && productName.trim() && settingsId && !saving && !isCustom;

    // Use the first existing product's model_id as test subject for price API
    const testModelId = useMemo(() => products.find((p) => p.model_id)?.model_id ?? null, [products]);

    useEffect(() => {
        if (!settingsId) {
            setIsCustom(false);
            setPrice(null);
            return;
        }

        let cancelled = false;
        setPriceLoading(true);
        setIsCustom(false);

        if (!testModelId) {
            // No test model available — fallback to local custom flag
            const app = productSettings?.find((a) => a.name === chosenConfig.application);
            const setting = app?.settings?.find((s) => s.id === settingsId);
            setIsCustom(!!setting?.custom);
            setPrice(null);
            setPriceLoading(false);
            return;
        }

        const s = chosenConfig;
        const params = new URLSearchParams();
        params.set("model_id", testModelId);
        params.set("amount", 1);
        const settingsMap = {
            application: s.application,
            material: s.material,
            color: s.color,
            polish: s.polish,
            coating: s.coating,
            orientation: s.orientation,
            sterile: s.sterile,
            intendedUse: s.intendedUse,
            clinicalUse: s.clinicalUse,
            custom: s.custom,
            case: s.case || "-",
            practitioner: s.practitioner || "-",
        };
        for (const [key, val] of Object.entries(settingsMap)) {
            params.set(key, val !== null && val !== undefined && val !== "" ? val : "null");
        }

        apiLabelUrl("api")
            .then((baseUrl) => {
                return axios.get(`${baseUrl}/product-price?${params.toString()}`);
            })
            .then(() => {
                if (!cancelled) {
                    setIsCustom(false);
                    setPriceLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setIsCustom(err.response?.status === 400);
                    setPriceLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [settingsId, testModelId]);

    // Dropzone
    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            const f = acceptedFiles[0];
            setFile(f);
            setFileExtension(f.name.split(".").pop().toLowerCase());
            setProductName((prev) => prev || f.name.replace(/\.[^/.]+$/, ""));

            const url = URL.createObjectURL(f);
            setModelPreviewUrl(url);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "model/stl": [".stl"],
            "model/step": [".stp", ".step"],
            "model/3mf": [".3mf"],
        },
        maxFiles: 1,
        maxSize: 200 * 1024 * 1024,
    });

    // Create a new label inline
    const handleCreateLabel = async () => {
        if (!newLabelName.trim() || savingLabel) return;
        setSavingLabel(true);
        try {
            const labelId = window.localStorage.getItem("label_id");
            const response = await axios.post(window.location.origin + `/api/${labelId}/library/labels`, {
                name: newLabelName.trim(),
                color: newLabelColor,
            });
            const created = response.data;
            onLabelCreated(created);
            setSelectedLabelIds((prev) => [...prev, created.id]);
            setShowNewLabel(false);
            setNewLabelName("");
            setNewLabelColor(presetColors[0]);
        } catch (err) {
            console.error("Failed to create label:", err);
        } finally {
            setSavingLabel(false);
        }
    };

    // Parse dimensions from the modelviewer-stats div (filled by ModelViewer)
    const parseDimensions = useCallback(() => {
        const container = document.getElementById(modelViewerContainerId);
        if (!container) return null;
        const stats = container.querySelector(".modelviewer-stats");
        if (!stats || stats.children.length === 0) return null;

        const dims = {};
        Array.from(stats.querySelectorAll("p")).forEach((p) => {
            const text = p.textContent;
            const num = parseFloat(text.replace(/[^0-9.]/g, ""));
            if (isNaN(num)) return;
            if (text.startsWith("Surface")) dims.surface = num;
            else if (text.startsWith("Volume")) dims.volume = num;
            else if (text.startsWith("Length")) dims.width = num;
            else if (text.startsWith("Width")) dims.length = num;
            else if (text.startsWith("Height")) dims.height = num;
        });

        return Object.keys(dims).length > 0 ? dims : null;
    }, []);

    // Capture screenshot from initial camera position
    const capturePreview = useCallback(() => {
        if (screenshotFnRef.current) {
            return screenshotFnRef.current();
        }
        // Fallback: capture canvas as-is
        const container = viewerContainerRef.current;
        if (!container) return null;
        const canvas = container.querySelector("canvas");
        if (!canvas) return null;
        return canvas.toDataURL("image/jpeg", 0.85);
    }, []);

    // Save product
    const handleSave = async () => {
        if (!canSave) return;

        setSaving(true);
        setError(null);

        const previewImage = capturePreview();

        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("name", productName.trim());
            formData.append("settings_id", settingsId);
            formData.append("default_quantity", defaultQuantity);
            if (previewImage) {
                formData.append("preview", previewImage);
            }
            if (selectedLabelIds.length > 0) {
                formData.append("label_ids", selectedLabelIds.join(","));
            }
            const dimensions = parseDimensions();
            if (dimensions) {
                formData.append("model_dimensions", JSON.stringify(dimensions));
            }

            const labelId = window.localStorage.getItem("label_id");
            const response = await axios.post(window.location.origin + `/api/${labelId}/library/products`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            // Fetch price in background, then notify parent with updated product
            const product = response.data;
            fetchLibraryProductPrice(product).then((updated) => {
                onProductAdded(updated);
            });
        } catch (err) {
            console.error("Failed to save library product:", err);
            setError(err.response?.data?.message || "Something went wrong while saving.");
            setSaving(false);
        }
    };

    return (
        <>
            <div className="library-slideover-overlay" onClick={onClose} />
            <div className="library-slideover">
                <div className="library-slideover-header">
                    <h3>Add product</h3>
                    <button className="library-slideover-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} size="lg" />
                    </button>
                </div>

                <div className="library-slideover-body">
                    {/* Upload zone / Preview */}
                    {!file ? (
                        <div {...getRootProps()} className={`library-dropzone ${isDragActive ? "active" : ""}`}>
                            <input {...getInputProps()} />
                            <FontAwesomeIcon
                                icon={faFileArrowUp}
                                size="2x"
                                style={{ color: "#022955", marginBottom: "10px" }}
                            />
                            <h4>Drop your file here</h4>
                            <p>or click to upload</p>
                            <p className="library-dropzone-formats">STL, STEP, 3MF</p>
                        </div>
                    ) : (
                        <div className="library-preview-section">
                            {/* Live interactive 3D viewer */}
                            <div className="library-model-preview" ref={viewerContainerRef}>
                                {modelPreviewUrl && (
                                    <LibraryModelViewer
                                        modelUrl={modelPreviewUrl}
                                        extension={fileExtension}
                                        containerId={modelViewerContainerId}
                                        screenshotFnRef={screenshotFnRef}
                                    />
                                )}
                            </div>
                            <div className="library-file-info">
                                <span>{file.name}</span>
                                <button
                                    className="library-file-remove"
                                    onClick={() => {
                                        setFile(null);
                                        setFileExtension(null);
                                        setModelPreviewUrl(null);
                                    }}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Product name */}
                    <div className="library-form-group">
                        <label>Product name</label>
                        <input
                            type="text"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                            placeholder="Give your product a name"
                            className="library-input"
                        />
                    </div>

                    {/* Labels */}
                    <div className="library-form-group">
                        <label>Labels</label>
                        <div className="library-label-select">
                            {labels.map((label) => (
                                <button
                                    key={label.id}
                                    type="button"
                                    className={`library-label-chip-select ${
                                        selectedLabelIds.includes(label.id) ? "active" : ""
                                    }`}
                                    style={label.color ? { "--label-color": label.color } : {}}
                                    onClick={() =>
                                        setSelectedLabelIds((prev) =>
                                            prev.includes(label.id)
                                                ? prev.filter((id) => id !== label.id)
                                                : [...prev, label.id]
                                        )
                                    }
                                >
                                    {label.color && (
                                        <span className="library-label-dot" style={{ backgroundColor: label.color }} />
                                    )}
                                    {label.name}
                                </button>
                            ))}
                            {!showNewLabel && (
                                <button
                                    type="button"
                                    className="library-label-add-btn"
                                    onClick={() => setShowNewLabel(true)}
                                >
                                    <FontAwesomeIcon icon={faPlus} /> New
                                </button>
                            )}
                        </div>

                        {showNewLabel && (
                            <div className="library-label-inline-form">
                                <input
                                    type="text"
                                    className="library-input"
                                    placeholder="Label name"
                                    value={newLabelName}
                                    onChange={(e) => setNewLabelName(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleCreateLabel()}
                                    autoFocus
                                />
                                <div className="library-color-picker">
                                    {presetColors.map((c, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            className={`library-color-swatch ${newLabelColor === c ? "active" : ""}`}
                                            style={{ backgroundColor: c }}
                                            onClick={() => setNewLabelColor(c)}
                                        />
                                    ))}
                                </div>
                                <div className="library-label-form-actions">
                                    <button
                                        type="button"
                                        className="library-btn-cancel"
                                        onClick={() => {
                                            setShowNewLabel(false);
                                            setNewLabelName("");
                                            setNewLabelColor(presetColors[0]);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="library-btn-save"
                                        onClick={handleCreateLabel}
                                        disabled={!newLabelName.trim() || savingLabel}
                                    >
                                        {savingLabel ? "Saving..." : "Add"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Settings via ProductSettings card UI */}
                    <div className="library-product-settings">
                        <ProductSettings
                            modelsData={[{ data: [] }]}
                            textData={textData}
                            updateModelSettings={() => {}}
                            saveProductSettings={() => {}}
                            updateSelectedModelsColor={() => {}}
                            updateModelDeliveryTime={() => {}}
                            updateModelOrientation={() => {}}
                            updatePricesOnNextIteration={() => {}}
                            setUpdatingMultipleModels={() => {}}
                            setSaveFooter={() => {}}
                            saveFooter={false}
                            disableProductSettings={false}
                            loggedInUser={loggedInUser}
                            labelLogo={labelLogo}
                            setChosenConfig={setChosenConfig}
                            productSettings={productSettings}
                            isLibrary={true}
                        />
                    </div>

                    {/* Default quantity */}
                    <div className="library-form-group">
                        <label>Default quantity</label>
                        <input
                            type="number"
                            min="1"
                            value={defaultQuantity}
                            onChange={(e) => setDefaultQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                            className="library-input library-input-quantity"
                        />
                    </div>

                    {/* No price available notice */}
                    {isCustom && !priceLoading && (
                        <div className="library-on-request-notice">
                            <span className="library-on-request-badge">Not available</span>
                            <p>
                                The selected material <strong>({chosenConfig.material})</strong> is not available for
                                the product library. Please choose a different configuration.
                            </p>
                        </div>
                    )}

                    {/* Error */}
                    {error && <div className="library-error">{error}</div>}
                </div>

                {/* Footer */}
                <div className="library-slideover-footer">
                    <button className="library-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="library-btn-save" onClick={handleSave} disabled={!canSave || priceLoading}>
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </>
    );
}
